<script setup lang="ts">
import { useBreakpoints } from '@vueuse/core';
import { breakpoints } from '#shared/lib/breakpoints';
import { DownArrowIcon, UpArrowIcon } from '#shared/components/icons';
import { formatDate } from '#shared/utils/format';
import { isSameHour, isSameMinute } from 'date-fns';
import { computed, ref } from 'vue';

const props = defineProps<{
  slots: Date[];
  maxSlots?: number;
  modelValue?: Date;
}>();
const emit = defineEmits<{
  (e: 'update:model-value', modelValue: Date): void;
}>();

const isSm = useBreakpoints(breakpoints).smaller('sm'); // TODO: rewrite to pure CSS

const isExpandable = computed(
  () => props.maxSlots && props.slots.length > props.maxSlots && !isSm.value
);
const isExpanded = ref(false);
const expandableTimeSlots = computed(() =>
  !isExpandable.value || isExpanded.value
    ? props.slots
    : props.slots.slice(0, props.maxSlots)
);

function updateModelValue(modelValue: Date) {
  emit('update:model-value', modelValue);
}
function isSameTime(date1: Date | number, date2: Date | number) {
  return isSameHour(date1, date2) && isSameMinute(date1, date2);
}
</script>

<template>
  <div>
    <AppButton
      v-for="timeSlot in expandableTimeSlots"
      :key="timeSlot.toString()"
      variant="primary"
      :color="
        props.modelValue && isSameTime(props.modelValue, timeSlot)
          ? 'primary'
          : 'white'
      "
      class="timepicker__time"
      @click="updateModelValue(timeSlot)"
    >
      {{ formatDate(timeSlot, { representation: 'time' }) }}
    </AppButton>
    <AppButton
      v-if="isExpandable"
      variant="secondary"
      color="dark"
      :icon-end="isExpanded ? UpArrowIcon : DownArrowIcon"
      class="timepicker__expand-button"
      @click="isExpanded = !isExpanded"
    />
  </div>
</template>

<style scoped lang="scss">
@import '#shared/assets/core.scss';

.timepicker {
  &__slots {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 8px;
    row-gap: 8px;

    @include media-breakpoint-down(sm) {
      flex-wrap: nowrap;
      overflow-x: scroll;
      justify-content: flex-start;
    }
  }

  &__time {
    padding: 8px 19px;
    font-variant-numeric: tabular-nums;
    transition: 0.15s ease;
  }

  &__expand-button {
    padding: 4px 30px;
    background-color: var(--app-color-white) !important;
  }

  &__sorry {
    color: var(--app-color-black-secondary);
  }
}
</style>
